import { FC, useRef, useState } from "react";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useMaintenanceTypesApi } from "../../../http/maintenanceTypes/api";
import { MaintenanceType } from "../../../http/maintenanceTypes/types";
import { PaginationQueryWithFields } from "../../../types";
import { Action, getDeleteAction, getEditAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import Table from "../../ui/Table";
import CreateMaintenanceTypeButton from "./CreateMaintenanceTypeButton";
import DeleteMaintenanceTypeDialog from "./DeleteMaintenanceTypeDialog";
import EditMaintenanceTypeDialog from "./EditMaintenanceTypeDialog";

const MAINTENANCE_TYPE_FIELDS = ["id", "name"] as const;
type MaintenanceTypeField = (typeof MAINTENANCE_TYPE_FIELDS)[number];
type MaintenanceTypeSearch = Pick<MaintenanceType, MaintenanceTypeField>;

interface MaintenanceTypesProps {
  onClose: () => void;
}

const MaintenanceTypes: FC<MaintenanceTypesProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const maintenanceTypesApi = useMaintenanceTypesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "mt" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());

  const [itemToEdit, setItemToEdit] = useState<MaintenanceTypeSearch>();
  const [itemToDelete, setItemToDelete] = useState<MaintenanceTypeSearch>();

  const query: PaginationQueryWithFields<MaintenanceTypeSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: MAINTENANCE_TYPE_FIELDS,
  };

  const result = maintenanceTypesApi.useAdminMaintenanceTypes(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: MaintenanceTypeSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.maintenanceTypes.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    if (userSettings.permissions.maintenanceTypes.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.maintenanceTypes.plural}
        preTitleButton={userSettings.permissions.maintenanceTypes.insert && <CreateMaintenanceTypeButton />}
      >
        <Table<MaintenanceTypeSearch>
          filters={filters}
          head={
            <>
              <Table.Th title={i18n.t.common.name} fieldName="maintenanceType.name" order={orderOptions} />
              <th></th>
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteMaintenanceTypeDialog maintenanceTypeId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && (
        <EditMaintenanceTypeDialog maintenanceTypeId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />
      )}
    </>
  );
};

export default MaintenanceTypes;
